'use client';

import { c as _c } from "react/compiler-runtime";
import { BlocksContent } from '@strapi/blocks-react-renderer';
import clsx from 'clsx';
import Image from 'next/image';
import { FC, useEffect, useRef } from 'react';
import MainAwardSegment from '@/components/Awards/AwardsAccordeon/MainAwardSegment';
import { AwardsDto } from '@/components/Awards/interface';
import TitleGraffiti from '@/components/shared/TitleGraffiti';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';
import AwardsList from './AwardsList';
import css from './style.module.scss';
interface Props {
  data: AwardsDto;
  title?: BlocksContent;
  subtitle?: string;
  curvedWrapperRelative?: boolean;
}
const AwardsAccordeon: FC<Props> = t0 => {
  const $ = _c(32);
  const {
    data,
    curvedWrapperRelative: t1
  } = t0;
  const curvedWrapperRelative = t1 === undefined ? false : t1;
  const wrapperRef = useRef(null);
  const tl = useRef(null);
  const device = useDevice();
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const wrapper = wrapperRef.current;
      const curvedText = wrapper?.querySelector(`.${css.curvedText}`);
      const bottomWrapper = wrapper?.querySelector(`.${css.curvedWrapper}`);
      const awardsWrapper = wrapper?.querySelector(`.${css.awardsWrapper}`);
      if (!wrapper || !curvedText || !bottomWrapper || !awardsWrapper) {
        return;
      }
      const ctx = gsap.context(() => {
        const matchMedia = gsap.matchMedia();
        matchMedia.add({
          isDesktop: "(min-width: 992px)",
          isMobile: "(max-width: 991px)"
        }, context => {
          const {
            isMobile
          } = context.conditions as gsap.Conditions;
          tl.current = gsap.timeline({
            scrollTrigger: {
              toggleActions: "play pause pause reverse",
              trigger: bottomWrapper,
              start: () => isMobile ? "bottom bottom" : `top-=${window.innerHeight * 0.05} bottom`,
              end: "bottom+=35% top",
              onUpdate: t4 => {
                const {
                  progress
                } = t4;
                const scrollProgress = progress * 360;
                gsap.to(curvedText, {
                  rotate: scrollProgress,
                  scale: 1.05,
                  ease: "none",
                  duration: 0
                });
              }
            }
          });
          if (isMobile) {
            return;
          }
          gsap.to(awardsWrapper, {
            y: "100%",
            scrollTrigger: {
              toggleActions: "play pause pause reverse",
              trigger: bottomWrapper,
              start: "center-=25%",
              end: "bottom",
              markers: false,
              scrub: true
            }
          });
          gsap.to(bottomWrapper, {
            y: "-25%",
            force3D: !isMobile,
            willChange: "transform",
            scrollTrigger: {
              toggleActions: "play pause pause reverse",
              trigger: bottomWrapper,
              start: "center-=25%",
              end: "bottom",
              markers: false,
              scrub: true
            }
          });
        });
      }, wrapper);
      return () => ctx.revert();
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== data.title) {
    t4 = <TitleGraffiti title={data.title} className={css.titleWrapper} url="/assets/video/mp4/awards-graffity.mp4" />;
    $[2] = data.title;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = curvedWrapperRelative ? css.curvedWrapperRelative : "";
  let t6;
  if ($[4] !== t5) {
    t6 = clsx(css.curvedWrapper, t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== device) {
    t7 = device === "desktop" && <div className={css.clear} />;
    $[6] = device;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== data.awardsItem) {
    t8 = data?.awardsItem ? <AwardsList items={data?.awardsItem} /> : null;
    $[8] = data.awardsItem;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <div className={css.awardsWrapper}>{t8}</div>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <MainAwardSegment />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t7 || $[14] !== t9) {
    t11 = <div className={css.curvedContainer}><div className={css.circle}>{t7}{t9}{t10}</div></div>;
    $[13] = t7;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = device === "desktop" ? 1775 : 424;
  const t13 = device === "desktop" ? 1775 : 424;
  const t14 = device === "desktop" ? "/assets/images/circle-text-white.webp" : "/assets/images/circle-text-mobile-white.webp";
  let t15;
  if ($[16] !== t12 || $[17] !== t13 || $[18] !== t14) {
    t15 = <Image width={t12} height={t13} alt="" className={css.curvedText} src={t14} />;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== device) {
    t16 = device !== "desktop" && <Image width={1775} height={1775} src="/assets/images/black-circle-mask.webp" alt="" className={css.circleMask} />;
    $[20] = device;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t15 || $[23] !== t16) {
    t17 = <div className={css.curvedContainer}>{t15}{t16}</div>;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t11 || $[26] !== t17 || $[27] !== t6) {
    t18 = <div className={css.bottomWrapper}><div className={t6}>{t11}{t17}</div></div>;
    $[25] = t11;
    $[26] = t17;
    $[27] = t6;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t18 || $[30] !== t4) {
    t19 = <section ref={wrapperRef} className={css.wrapper}>{t4}{t18}</section>;
    $[29] = t18;
    $[30] = t4;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  return t19;
};
export default AwardsAccordeon;